"use client";

import { useCallback, useRef, useState, type ReactNode, type RefObject } from "react";
import { sendGTMEvent } from "@next/third-parties/google";
import { LEAD_VALUES } from "@/utils/ga4-events";
import { X } from "lucide-react";
import type { IntentVehicleContext } from "@/types/prequalify";
import { useRouteOneFinancing } from "@/hooks/useRouteOneFinancing";
import { useFormFlow } from "@/hooks/useFormFlow";
import { fieldFromTarget } from "@/lib/analytics/formFields";
import ModalDialog from "@/components/common/ModalDialog";
import { PreQualifyGate, PREQUALIFY_GATE_DESCRIPTION_ID, PREQUALIFY_GATE_HEADING_ID, type PreQualifyGateContact } from "@/app/(public)/pre-qualify/PreQualifyGate";
interface RouteOneFinancingButtonProps {
  children: ReactNode;
  vehicle?: IntentVehicleContext | undefined;
  configuredUrl?: string | null | undefined;
  className?: string;
  ariaLabel?: string;
  /** Where the click happened, for the GTM event. */
  pageSection?: string;
  buttonId?: string;
  triggerRef?: RefObject<HTMLButtonElement | null>;
  /** Extra click handler for surface-specific analytics. */
  onClick?: (() => void) | undefined;
}

/**
 * Opens the RouteOne application behind a first-party contact gate.
 *
 * RouteOne runs in a new tab we cannot see into, so an ungated click produced
 * an intent with no identity — matchable back to the resulting CRM lead only by
 * timing. The gate captures contact details so later CRM matching can compare identity.
 *
 * A blank tab is reserved during submit, then navigated only after the contact
 * capture is saved. Popup-blocked shoppers receive a continuation button in the original tab.
 */
export default function RouteOneFinancingButton({
  children,
  vehicle,
  configuredUrl,
  className = "",
  ariaLabel = "Start your RouteOne financing application",
  pageSection = "finance_page",
  buttonId,
  triggerRef,
  onClick
}: RouteOneFinancingButtonProps) {
  const {
    openRouteOneFinancing
  } = useRouteOneFinancing(configuredUrl);
  const [isGateOpen, setGateOpen] = useState(false);
  const [contactDraft, setContactDraft] = useState<PreQualifyGateContact>();
  const [isSubmitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [continuation, setContinuation] = useState<{
    url: string;
    record: () => Promise<unknown>;
  } | null>(null);
  const submittingRef = useRef(false);
  const submittedContactRef = useRef<string | undefined>(undefined);
  const submissionIdRef = useRef<string | undefined>(undefined);
  const nameFieldRef = useRef<HTMLInputElement>(null);
  const flow = useFormFlow("financing");
  const closeGate = useCallback(() => {
    if (!submittingRef.current) setGateOpen(false);
  }, []);
  const handleClick = useCallback(() => {
    onClick?.();
    flow.reset();
    setSubmitError(null);
    setContinuation(null);
    submissionIdRef.current = crypto.randomUUID();
    submittedContactRef.current = undefined;
    try {
      sendGTMEvent({
        event: "financing_gate_open",
        form_name: "financing",
        category: "financing",
        label: "external_application_gate_open",
        finance_partner: "routeone",
        finance_type: "external_application",
        page_section: pageSection
      });
    } catch (error) {
      console.error("Analytics tracking error:", error);
    }
    setGateOpen(true);
  }, [pageSection, flow, onClick]);
  const handleGateSubmit = useCallback(async (contact: PreQualifyGateContact) => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    setSubmitError(null);
    const contactKey = JSON.stringify(contact);
    if (submittedContactRef.current && submittedContactRef.current !== contactKey) {
      submissionIdRef.current = crypto.randomUUID();
    }
    submittedContactRef.current = contactKey;
    flow.submit("contact");
    try {
      const analyticsAttemptId = flow.getAttemptId();
      const result = await openRouteOneFinancing({
        contact,
        pageSection,
        ...(submissionIdRef.current ? {
          submissionId: submissionIdRef.current
        } : {}),
        ...(vehicle ? {
          vehicle
        } : {}),
        ...(analyticsAttemptId ? {
          analyticsAttemptId
        } : {})
      });
      if (!result.success) {
        flow.error("request_failed", "contact");
        setSubmitError(result.error);
        return;
      }
      flow.complete("contact");
      try {
        sendGTMEvent({
          event: "generate_lead",
          form_name: "financing",
          flow_outcome: "captured",
          finance_partner: "routeone",
          finance_type: "external_application",
          page_section: pageSection,
          value: LEAD_VALUES.financing,
          currency: "USD"
        });
      } catch {/* Analytics must not interrupt a saved lead. */}
      if (result.handoffUrl && result.recordHandoff) {
        setContinuation({
          url: result.handoffUrl,
          record: result.recordHandoff
        });
      } else {
        flow.handoff();
        setGateOpen(false);
      }
    } catch {
      flow.error("request_failed", "contact");
      setSubmitError("We could not continue to RouteOne. Please try again.");
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  }, [openRouteOneFinancing, pageSection, flow, vehicle]);
  return <>
            <button {...buttonId ? {
      id: buttonId
    } : {}} type="button" ref={triggerRef} onClick={handleClick} className={className} aria-haspopup="dialog" aria-expanded={isGateOpen} aria-label={ariaLabel}>
                {children}
            </button>

            <ModalDialog open={isGateOpen} mobileBottomSheet onClose={closeGate} labelledBy={PREQUALIFY_GATE_HEADING_ID} describedBy={PREQUALIFY_GATE_DESCRIPTION_ID} initialFocusRef={nameFieldRef} panelClassName="sm:t-max-w-md sm:t-rounded-2xl t-bg-white t-shadow-2xl">
                <div className="t-sticky t-top-0 t-z-10 t-flex t-justify-end t-bg-white t-px-2 t-pt-2">
                    <span aria-hidden="true" className="t-absolute t-left-1/2 t-top-3 t-h-1 t-w-10 -t-translate-x-1/2 t-rounded-full t-bg-gray-300 sm:t-hidden" />
                    <button type="button" onClick={closeGate} className="t-inline-flex t-h-11 t-w-11 t-items-center t-justify-center t-rounded-full t-text-brand-ink t-transition-colors hover:t-bg-brand-surface focus:t-outline-none focus-visible:t-ring-2 focus-visible:t-ring-brand-violet focus-visible:t-ring-offset-2 motion-reduce:t-transition-none" aria-label="Close and stay on this page">
                        <X className="t-h-6 t-w-6" aria-hidden="true" />
                    </button>
                </div>
                <div className="t-px-5 t-pb-5 sm:t-px-7 sm:t-pb-7">
                    {continuation ? <div>
                            <h2 id={PREQUALIFY_GATE_HEADING_ID} className="t-text-xl t-font-semibold">Your details are saved.</h2>
                            <p id={PREQUALIFY_GATE_DESCRIPTION_ID} className="t-my-4">Continue your pre-qualification.</p>
                            <button type="button" className="t-inline-flex t-min-h-12 t-items-center t-rounded-full t-bg-brand-ink t-px-6 t-font-semibold t-text-white" onClick={async () => {
            await continuation.record().catch(() => {});
            flow.handoff();
            window.location.assign(continuation.url);
          }}>
                                Continue pre-qualification
                            </button>
                        </div> : <div onInputCapture={event => flow.field(fieldFromTarget(event.target))}><PreQualifyGate frameless description="Start with your contact details, then continue your pre-qualification in a new tab." submitLabel="Continue pre-qualification" initialFocusRef={nameFieldRef} onSubmit={handleGateSubmit} isSubmitting={isSubmitting} initialContact={contactDraft} onContactChange={setContactDraft} onInteraction={() => flow.begin("contact")} onValidationError={fields => {
            flow.error("validation", "contact");
            fields.forEach(field => flow.fieldError(field));
          }} /></div>}
                    {submitError && <p role="alert" className="t-mt-3 t-text-sm t-text-brand-rose">{submitError}</p>}
                    <button type="button" onClick={closeGate} className="t-mt-3 t-inline-flex t-min-h-[44px] t-w-full t-items-center t-justify-center t-rounded-full t-text-sm t-font-medium t-text-brand-ink-muted hover:t-text-brand-ink focus:t-outline-none focus-visible:t-ring-2 focus-visible:t-ring-brand-violet focus-visible:t-ring-offset-2">
                        Cancel
                    </button>
                </div>
            </ModalDialog>
        </>;
}